import jsPDF from 'jspdf';
import 'jspdf-autotable';

const fmt = (n) => {
  if (!n && n !== 0) return '-';
  const num = Math.round(n);
  const s = num.toString();
  const last3 = s.substring(s.length - 3);
  const rest = s.substring(0, s.length - 3);
  return 'Rs. ' + (rest ? rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' : '') + last3;
};

const fmtDate = (d) => {
  if (!d) return '-';
  try { return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

const loadImageAsBase64 = (url) => {
  return new Promise((resolve) => {
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const maxW = 300;
      const scale = img.width > maxW ? maxW / img.width : 1;
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      try { resolve({ data: canvas.toDataURL('image/jpeg', 0.6), w: canvas.width, h: canvas.height }); }
      catch { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
};

/**
 * Generate single-page A4 Order Form PDF
 */
export const printOrderForm = async (order, variant = 'customer') => {
  const isCustomer = variant === 'customer';
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pw = doc.internal.pageSize.getWidth(); // 210
  const ph = doc.internal.pageSize.getHeight(); // 297
  const m = 10; // margin
  const cw = pw - m * 2; // content width
  const col2X = pw / 2 + 3;
  let y = 10;

  // ===== HEADER =====
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('ORDER FORM', pw / 2, y, { align: 'center' });
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(120);
  doc.text(isCustomer ? 'Customer Copy' : 'Production Copy', pw / 2, y + 4.5, { align: 'center' });
  doc.setTextColor(0);
  y += 7;

  // Thin divider
  doc.setDrawColor(7, 94, 84);
  doc.setLineWidth(0.5);
  doc.line(m, y, pw - m, y);
  y += 4;

  // ===== COMPACT INFO ROWS =====
  const addField = (label, value, x, yy, maxW) => {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6);
    doc.setTextColor(130);
    doc.text(label, x, yy);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(0);
    const val = String(value || '-');
    const truncated = maxW ? val.substring(0, maxW) : val;
    doc.text(truncated, x, yy + 3.5);
  };

  // Row 1: Order #, System #, Type, Status
  addField('Order #', order.order_number, m, y);
  addField('System #', order.auto_generated_number, m + 45, y);
  addField('Type', order.order_type, m + 90, y);
  addField('Status', order.order_status, m + 135, y);
  y += 9;

  // Row 2: Product, Main Group, Delivery, Created
  addField('Product', order.title, m, y);
  addField('Main Group', order.main_group, m + 45, y);
  addField('Delivery', fmtDate(order.delivery_date), m + 90, y);
  addField('Created', fmtDate(order.created_at), m + 135, y);
  y += 9;

  // Row 3: Job Card (if exists)
  if (order.job_card_number) {
    addField('Job Card', order.job_card_number, m, y);
    y += 9;
  }

  // ===== CUSTOMER DETAILS (Customer copy only) =====
  if (isCustomer) {
    doc.setDrawColor(220);
    doc.setLineWidth(0.2);
    doc.line(m, y, pw - m, y);
    y += 3;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text('Customer', m, y);
    y += 4;

    addField('Name', order.customer_name, m, y);
    addField('Phone', order.customer_phone, m + 60, y);
    if (order.customer_address) {
      addField('Address', order.customer_address, m + 120, y, 30);
    }
    y += 9;
  }

  // ===== SPECS TABLE =====
  doc.setDrawColor(220);
  doc.setLineWidth(0.2);
  doc.line(m, y, pw - m, y);
  y += 3;

  const specRows = [];
  if (order.product_weight) specRows.push(['Weight', `${order.product_weight}g`]);
  if (order.metal_type) specRows.push(['Metal', order.metal_type]);
  if (order.karat_purity) specRows.push(['Karat', order.karat_purity]);
  if (order.stone_details) specRows.push(['Stones', order.stone_details]);
  if (order.hallmarking_needed) specRows.push(['Hallmark', 'Yes']);
  if (order.gold_rate_type) specRows.push(['Rate Type', order.gold_rate_type === 'fixed' ? 'Fixed' : 'Open']);
  if (isCustomer) {
    if (order.gold_rate_per_gram) specRows.push(['Gold Rate/g', fmt(order.gold_rate_per_gram)]);
    if (order.price_amount) specRows.push(['Amount', fmt(order.price_amount)]);
    if (order.advance_payment) specRows.push(['Advance', fmt(order.advance_payment)]);
  }
  if (!isCustomer) {
    if (order.assigned_worker_name) specRows.push(['Worker', order.assigned_worker_name]);
    if (order.current_stage) specRows.push(['Stage', order.current_stage]);
    if (order.required_gold_weight) specRows.push(['Req. Gold', `${order.required_gold_weight}g`]);
    if (order.required_diamond_weight) specRows.push(['Req. Diamond', `${order.required_diamond_weight}ct (${order.required_diamond_count || '-'} pcs)`]);
    if (order.required_stone_type) specRows.push(['Req. Stone', `${order.required_stone_type} ${order.required_stone_weight || ''}ct`]);
  }

  if (specRows.length > 0) {
    // Render as compact 2-column key-value (not full table)
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text(isCustomer ? 'Specifications & Pricing' : 'Specifications & Production', m, y);
    y += 4;

    doc.autoTable({
      startY: y,
      body: specRows,
      margin: { left: m, right: m },
      styles: { fontSize: 7, cellPadding: 1.5 },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 35, textColor: [80, 80, 80] } },
      theme: 'plain',
      didDrawCell: (data) => {
        if (data.row.index < specRows.length) {
          doc.setDrawColor(240);
          doc.setLineWidth(0.1);
          doc.line(data.cell.x, data.cell.y + data.cell.height, data.cell.x + data.cell.width, data.cell.y + data.cell.height);
        }
      },
    });
    y = doc.lastAutoTable.finalY + 3;
  }

  // ===== SOLITAIRES (compact) =====
  if (order.solitaires && order.solitaires.length > 0) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text('Solitaires', m, y);
    y += 3;

    const solRows = order.solitaires.map((s, i) => [
      s.shape || '-',
      s.size_from && s.size_to ? `${s.size_from}-${s.size_to}` : '-',
      (s.color_grades || []).join(',') || '-',
      (s.clarities || []).join(',') || '-',
      s.cut || '-',
      s.source === 'to_source' ? 'Source' : 'Stock',
    ]);

    doc.autoTable({
      startY: y,
      head: [['Shape', 'Size(ct)', 'Colour', 'Clarity', 'Cut', 'Src']],
      body: solRows,
      margin: { left: m, right: m },
      styles: { fontSize: 6.5, cellPadding: 1.2 },
      headStyles: { fillColor: [7, 94, 84], textColor: 255, fontSize: 6 },
    });
    y = doc.lastAutoTable.finalY + 3;
  }

  // ===== SPECIAL INSTRUCTIONS (truncated) =====
  if (order.special_instructions) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text('Instructions', m, y);
    y += 3;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    const maxChars = 200;
    const text = order.special_instructions.length > maxChars
      ? order.special_instructions.substring(0, maxChars) + '...'
      : order.special_instructions;
    const lines = doc.splitTextToSize(text, cw);
    doc.text(lines, m, y);
    y += Math.min(lines.length, 4) * 3.5 + 2;
  }

  // ===== PRIMARY PRODUCT IMAGE (4" x 4" = ~100mm) =====
  const primaryImg = order.primary_image || (order.uploaded_files || []).find(f => {
    if (!f) return false;
    const lower = f.toLowerCase();
    return lower.match(/\.(jpg|jpeg|png|webp|gif)/) || lower.includes('/uploads/');
  });

  if (primaryImg) {
    const remainingH = ph - y - 12;
    const imgSize = Math.min(100, remainingH - 5, cw); // 100mm = ~4", capped by remaining space

    if (imgSize > 30) {
      doc.setDrawColor(220);
      doc.setLineWidth(0.2);
      doc.line(m, y, pw - m, y);
      y += 3;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(0);
      doc.text('Product Image', m, y);
      y += 4;

      const imgData = await loadImageAsBase64(primaryImg);
      if (imgData) {
        const aspect = imgData.h / imgData.w;
        let drawW = imgSize;
        let drawH = drawW * aspect;
        if (drawH > imgSize) { drawH = imgSize; drawW = drawH / aspect; }
        // Center the image
        const imgX = m + (cw - drawW) / 2;
        doc.addImage(imgData.data, 'JPEG', imgX, y, drawW, drawH);
        y += drawH + 3;
      }
    }
  }

  // ===== FOOTER =====
  const footerY = ph - 8;
  doc.setDrawColor(200);
  doc.setLineWidth(0.2);
  doc.line(m, footerY - 3, pw - m, footerY - 3);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6);
  doc.setTextColor(150);
  doc.text(`Generated: ${new Date().toLocaleString('en-IN')}`, m, footerY);
  doc.text(isCustomer ? 'Customer Copy' : 'Production Copy', pw - m, footerY, { align: 'right' });

  const filename = `${order.order_number}_${variant}_copy.pdf`;
  doc.save(filename);
  return filename;
};

/**
 * Generate blob URL for sharing (async)
 */
export const getOrderFormBlobUrl = async (order, variant = 'customer') => {
  // Simplified version without images for quick share
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  doc.setFontSize(12);
  doc.text(`Order: ${order.order_number}`, 15, 20);
  return doc.output('bloburl');
};
